/**
 * OS notifications for events this process received itself.
 *
 * ROADMAP section 3 excludes push notifications on principle: they need a gateway
 * that holds your subscription list, watches your inbox and knows when you are
 * online. None of that applies here. The events are ones the app's own relay
 * sockets delivered, the notifier is the local desktop, and no server learns
 * anything it did not already see. That is the whole distinction the roadmap draws
 * between "not building" and "revisit in the desktop shell".
 *
 * Which means the limits are honest and are not worked around: there is no
 * background delivery. Setu must be running to notice anything, exactly as a tab
 * must be open for a subscription to exist.
 */

import { useEffect, useMemo, useRef } from "react";
import type { AuthorView } from "../notes/types";
import type { NotificationItem } from "../notifications/groupNotifications";
import { useNotifications } from "../notifications/useNotifications";
import { fallbackAuthor, useAuthors } from "../profiles/useAuthors";
import {
  type NoticeText,
  noticeForItem,
  planNotices,
  summaryNotice,
} from "./desktopNotice";
import { invokeNative } from "./runtime";

/**
 * How many rows' actors are resolved to names.
 *
 * A bounded slice rather than the whole list: this hook is mounted for the whole
 * session, and asking for every actor's profile would put a subscription behind a
 * feature the reader may never see fire. The newest rows are the only ones that can
 * still produce a notification.
 */
const NAMED_ROWS = 20;

/**
 * Ceiling on the remembered row keys, so a long session cannot grow the set without
 * bound. Well above the number of rows held live, so trimming never re-notifies
 * something still on screen.
 */
const MAX_REMEMBERED = 600;

interface Progress {
  viewer?: string;
  /** Only rows newer than this are candidates, in seconds. */
  floor: number;
  /** Row keys already delivered — or deliberately dropped. */
  seen: Set<string>;
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

/**
 * Whether the app is in front of the user right now.
 *
 * A notification for something already on screen is noise, and the desktop
 * convention is to stay quiet while focused. `hasFocus` and `visibilityState` are
 * both consulted because they answer different questions: a window can be visible
 * and not focused (side by side with an editor), and focused-but-hidden is what a
 * minimised window reports on some platforms.
 */
function inForeground(): boolean {
  if (typeof document === "undefined") return false;
  return document.visibilityState === "visible" && document.hasFocus();
}

function actorNames(
  item: NotificationItem,
  authors: ReadonlyMap<string, AuthorView>,
): string[] {
  return item.actors.map((actor) => {
    // NIP-57 permits a receipt that names no sender, and inventing one would be
    // worse than saying we do not know.
    if (!actor.pubkey) return "Anonymous";
    const author = authors.get(actor.pubkey) ?? fallbackAuthor(actor.pubkey);
    return author.displayName;
  });
}

/** Fire and forget: a notifier that refuses is not a reason to break a render. */
function show(notice: NoticeText): void {
  void invokeNative<null>("show_local_notice", {
    title: notice.title,
    body: notice.body,
  });
}

/**
 * Notify about notification rows that appear while the app is running.
 *
 * Mounted once, from the desktop bridge. Three rules keep it from being a nuisance:
 *
 *  - **A floor at start-up.** Only rows newer than the moment this began watching
 *    are candidates, so the store's replay of a month of history on launch is not a
 *    month of notifications.
 *  - **One notice per row, ever.** Rows are grouped, so twenty likes on one note is
 *    one row; a twenty-first like updates a row that has already been announced and
 *    says nothing further.
 *  - **A burst becomes a count.** See `planNotices`.
 */
export function useDesktopNotifications(): void {
  const { items, viewerPubkey } = useNotifications();

  const namedPubkeys = useMemo(() => {
    const out: string[] = [];
    for (const item of items.slice(0, NAMED_ROWS)) {
      for (const actor of item.actors) {
        if (actor.pubkey && !out.includes(actor.pubkey)) out.push(actor.pubkey);
      }
    }
    return out;
  }, [items]);
  const authors = useAuthors(namedPubkeys);

  // A ref, not state: recording what has been announced must not cause a render,
  // and a render caused by it would re-run this effect with the same rows.
  const progress = useRef<Progress>({ floor: 0, seen: new Set() });

  useEffect(() => {
    if (!viewerPubkey) return;
    const state = progress.current;

    if (state.viewer !== viewerPubkey) {
      // A different inbox entirely. Nothing carries over — announcing the new
      // account's backlog on a switch is the same mistake as announcing it on
      // launch, and the previous account's row keys mean nothing here.
      state.viewer = viewerPubkey;
      state.floor = nowSeconds();
      state.seen = new Set();
    }

    const pending = items.filter(
      (item) => item.createdAt > state.floor && !state.seen.has(item.key),
    );
    if (pending.length === 0) return;

    /*
     * Marked before the foreground check, on purpose.
     *
     * Anything that arrives while the user is looking at the app has been seen by
     * definition, so it must not be queued up and fired when they alt-tab away.
     * The alternative is a burst of notices about things already read, which is how
     * a notifier trains its user to turn it off.
     */
    for (const item of pending) state.seen.add(item.key);
    if (state.seen.size > MAX_REMEMBERED) {
      state.seen = new Set([...state.seen].slice(-MAX_REMEMBERED));
    }

    if (inForeground()) return;

    const { show: individual, summarised } = planNotices(pending);
    for (const item of individual) {
      show(noticeForItem(item, actorNames(item, authors)));
    }
    if (summarised > 0) show(summaryNotice(summarised));
  }, [items, viewerPubkey, authors]);
}
